import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit3,
  ExternalLink,
  Loader2,
  Mail,
  Plus,
  Search,
  Video,
} from 'lucide-react';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';

const STORAGE_KEY = 'dad-partners-admin-marketing-content-v1';
const DEFAULT_DISTRIBUTION = {
  blog: false,
  email: false,
  facebook: false,
  instagram: false,
};

const CONTENT_TYPE_META = {
  youtube: {
    label: 'YouTube',
    badgeClass: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
  },
  blog: {
    label: 'Blog',
    badgeClass: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  },
};

const STAGE_META = {
  idea: {
    label: 'Idea',
    badgeClass: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  },
  outlined: {
    label: 'Outlined',
    badgeClass: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  },
  recorded: {
    label: 'Recorded',
    badgeClass: 'border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300',
  },
  published: {
    label: 'Published',
    badgeClass: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  },
};

const STAGE_ACTION_LABELS = {
  outlined: 'Outline',
  recorded: 'Record',
  published: 'Publish',
};

const RESEARCH_CATEGORIES = [
  {
    id: 'fatherhood',
    label: 'Dads & Fatherhood',
    subreddits: ['daddit', 'Fatherhood'],
  },
  {
    id: 'marriage',
    label: 'Marriage & Family',
    subreddits: ['marriage', 'marriageadvice'],
  },
  {
    id: 'faith',
    label: 'Faith & Christianity',
    subreddits: ['Christianity', 'ChristianParenting'],
  },
  {
    id: 'work',
    label: 'Work & Career',
    subreddits: ['daddit'],
    keywords: ['work', 'job', 'career', 'boss', 'money'],
  },
];

const DISTRIBUTION_FIELDS = [
  { key: 'blog', label: 'Blog posted' },
  { key: 'email', label: 'Email sent' },
  { key: 'facebook', label: 'Facebook posted' },
  { key: 'instagram', label: 'Instagram posted' },
];

const createEmptyDraft = (overrides = {}) => ({
  title: '',
  type: 'youtube',
  plannedDate: '',
  ...overrides,
});

const getStageSequence = (type) => (
  type === 'youtube'
    ? ['idea', 'outlined', 'recorded', 'published']
    : ['idea', 'outlined', 'published']
);

const getNextStage = (item) => {
  const stages = getStageSequence(item.type);
  const currentIndex = stages.indexOf(item.stage);
  if (currentIndex < 0 || currentIndex >= stages.length - 1) return null;
  return stages[currentIndex + 1];
};

const formatPlannedDate = (value) => {
  if (!value) return 'No date set';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'No date set';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const truncateTitle = (value, maxLength = 120) => {
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
};

const sanitizeStoredItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === 'object' && item.title)
    .map((item) => ({
      id: item.id || String(Date.now()),
      title: String(item.title || '').trim(),
      type: item.type === 'blog' ? 'blog' : 'youtube',
      plannedDate: String(item.plannedDate || ''),
      stage: getStageSequence(item.type === 'blog' ? 'blog' : 'youtube').includes(item.stage)
        ? item.stage
        : 'idea',
      distribution: {
        ...DEFAULT_DISTRIBUTION,
        ...(item.distribution || {}),
      },
      completedAt: item.completedAt ? String(item.completedAt) : null,
    }));
};

const getPlannedDateSortValue = (value) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(`${value}T00:00:00`).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

const sortActiveItems = (items) => (
  [...items].sort((left, right) => {
    const leftDate = getPlannedDateSortValue(left.plannedDate);
    const rightDate = getPlannedDateSortValue(right.plannedDate);
    if (leftDate !== rightDate) return leftDate - rightDate;
    return Number(left.id) - Number(right.id);
  })
);

const sortCompletedItems = (items) => (
  [...items].sort((left, right) => {
    const leftTime = left.completedAt ? new Date(left.completedAt).getTime() : 0;
    const rightTime = right.completedAt ? new Date(right.completedAt).getTime() : 0;
    return rightTime - leftTime;
  })
);

const formatCompletedDate = (value) => {
  if (!value) return 'Completed date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Completed date unavailable';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getCheckedDistributionLabels = (distribution = {}) => (
  DISTRIBUTION_FIELDS
    .filter((field) => Boolean(distribution?.[field.key]))
    .map((field) => field.label)
);

const toQueueApiPayload = (item) => ({
  item_id: item.id,
  title: item.title,
  type: item.type,
  planned_date: item.plannedDate || '',
  stage: item.stage,
  distribution: item.distribution || DEFAULT_DISTRIBUTION,
  completed_at: item.completedAt || null,
});

const fromQueueApiItem = (item) => sanitizeStoredItems([{
  id: item?.item_id,
  title: item?.title,
  type: item?.type,
  plannedDate: item?.planned_date,
  stage: item?.stage,
  distribution: item?.distribution,
  completedAt: item?.completed_at,
}])[0];

async function fetchResearchCategory(category) {
  const responses = await Promise.all(
    (category.subreddits || []).map(async (subreddit) => {
      const response = await fetch(`https://www.reddit.com/r/${subreddit}/top.json?t=week&limit=10`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load ${subreddit}`);
      }

      const payload = await response.json();
      const children = Array.isArray(payload?.data?.children) ? payload.data.children : [];
      return children.map((child) => child?.data).filter(Boolean);
    }),
  );

  const keywords = (category.keywords || []).map((keyword) => keyword.toLowerCase());
  const merged = responses.flat().filter((post) => {
    if (!keywords.length) return true;
    const haystack = `${post?.title || ''} ${post?.selftext || ''}`.toLowerCase();
    return keywords.some((keyword) => haystack.includes(keyword));
  });

  const deduped = Array.from(
    merged.reduce((map, post) => {
      if (post?.id && !map.has(post.id)) {
        map.set(post.id, post);
      }
      return map;
    }, new Map()).values(),
  );

  return deduped
    .sort((a, b) => Number(b?.score || 0) - Number(a?.score || 0))
    .map((post) => ({
      id: post.id,
      title: post.title || 'Untitled post',
      score: Number(post.score || 0),
      subreddit: post.subreddit_name_prefixed || `r/${post.subreddit || 'reddit'}`,
      permalink: post.permalink ? `https://www.reddit.com${post.permalink}` : null,
    }));
}

export default function MarketingContentTab({
  adminFetch = null,
  externalDraft = null,
  onExternalDraftConsumed = null,
}) {
  const [items, setItems] = useState(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return sanitizeStoredItems(JSON.parse(stored));
    } catch {
      return [];
    }
  });
  const initialLocalItemsRef = useRef(null);
  if (initialLocalItemsRef.current === null) initialLocalItemsRef.current = items;
  const [syncFailed, setSyncFailed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(() => createEmptyDraft());
  const [researchOpen, setResearchOpen] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(RESEARCH_CATEGORIES[0].id);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchError, setResearchError] = useState('');
  const [researchResults, setResearchResults] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [completedOpen, setCompletedOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const syncQueueItem = useCallback(async (item, method = 'POST') => {
    if (typeof adminFetch !== 'function') return;
    const path = method === 'POST'
      ? '/admin/marketing/content-queue'
      : `/admin/marketing/content-queue/${encodeURIComponent(item.id)}`;
    try {
      const response = await adminFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toQueueApiPayload(item)),
      });
      if (!response.ok) throw new Error(`queue sync failed (${response.status})`);
      setSyncFailed(false);
    } catch (error) {
      setSyncFailed(true);
      toast.error('The queue is saved locally, but server sync needs attention.');
    }
  }, [adminFetch]);

  useEffect(() => {
    if (typeof adminFetch !== 'function') return undefined;
    let cancelled = false;

    const loadQueue = async () => {
      try {
        const response = await adminFetch('/admin/marketing/content-queue');
        if (!response.ok) throw new Error(`queue load failed (${response.status})`);
        const payload = await response.json();
        const remoteItems = Array.isArray(payload?.items)
          ? payload.items.map(fromQueueApiItem).filter(Boolean)
          : [];
        const remoteIds = new Set(remoteItems.map((item) => item.id));
        const localOnlyItems = (initialLocalItemsRef.current || []).filter((item) => !remoteIds.has(item.id));
        await Promise.all(localOnlyItems.map((item) => syncQueueItem(item)));
        if (!cancelled) {
          setItems((current) => {
            const currentOnly = current.filter((item) => !remoteIds.has(item.id));
            return [...remoteItems, ...currentOnly];
          });
        }
      } catch (error) {
        if (!cancelled) setSyncFailed(true);
      }
    };

    loadQueue();
    return () => {
      cancelled = true;
    };
  }, [adminFetch, syncQueueItem]);

  useEffect(() => {
    if (!externalDraft?.requestKey) return;
    setEditingItemId(null);
    setDraft(createEmptyDraft({
      title: externalDraft.title || '',
      type: externalDraft.type || 'youtube',
      plannedDate: externalDraft.plannedDate || '',
    }));
    setDialogOpen(true);
    if (typeof onExternalDraftConsumed === 'function') {
      onExternalDraftConsumed();
    }
  }, [externalDraft?.requestKey, onExternalDraftConsumed]);

  const activeCategory = useMemo(
    () => RESEARCH_CATEGORIES.find((category) => category.id === activeCategoryId) || RESEARCH_CATEGORIES[0],
    [activeCategoryId],
  );

  const activeItems = useMemo(() => items.filter((item) => !item.completedAt), [items]);
  const completedItems = useMemo(() => sortCompletedItems(items.filter((item) => item.completedAt)), [items]);
  const youtubeItems = useMemo(() => sortActiveItems(activeItems.filter((item) => item.type === 'youtube')), [activeItems]);
  const blogItems = useMemo(() => sortActiveItems(activeItems.filter((item) => item.type === 'blog')), [activeItems]);

  const stats = useMemo(() => ({
    total: items.length,
    published: activeItems.filter((item) => item.stage === 'published').length,
    youtube: items.filter((item) => item.type === 'youtube').length,
    distributed: completedItems.length,
  }), [activeItems, completedItems.length, items]);

  const loadResearchResults = async (category, isCancelled = () => false) => {
    setResearchLoading(true);
    setResearchError('');
    try {
      const results = await fetchResearchCategory(category);
      if (!isCancelled()) {
        setResearchResults(results);
      }
    } catch (error) {
      if (!isCancelled()) {
        setResearchResults([]);
        setResearchError("Couldn't load Reddit right now");
      }
    } finally {
      if (!isCancelled()) {
        setResearchLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!researchOpen) return;
    let cancelled = false;

    loadResearchResults(activeCategory, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [activeCategory, researchOpen]);

  const updateDraft = (field, value) => {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetDraft = () => {
    setDraft(createEmptyDraft());
    setEditingItemId(null);
  };

  const openAddDialog = (overrides = {}) => {
    setDraft(createEmptyDraft(overrides));
    setDialogOpen(true);
  };

  const handleAddItem = () => {
    if (!draft.title.trim()) {
      toast.error('Add a title before saving this content piece.');
      return;
    }

    if (editingItemId) {
      const updatedItem = items.find((item) => item.id === editingItemId);
      const nextItem = updatedItem ? {
        ...updatedItem,
        title: draft.title.trim(),
        plannedDate: draft.plannedDate,
      } : null;
      setItems((current) => current.map((item) => (
        item.id === editingItemId
          ? nextItem
          : item
      )));
      if (nextItem) syncQueueItem(nextItem, 'PATCH');
    } else {
      const newItem = {
        id: String(Date.now()),
        title: draft.title.trim(),
        type: draft.type === 'blog' ? 'blog' : 'youtube',
        plannedDate: draft.plannedDate,
        stage: 'idea',
        distribution: { ...DEFAULT_DISTRIBUTION },
        completedAt: null,
      };

      setItems((current) => [newItem, ...current]);
      syncQueueItem(newItem);
    }

    setDialogOpen(false);
    resetDraft();
  };

  const handleAdvanceStage = (itemId) => {
    const item = items.find((entry) => entry.id === itemId);
    const nextStage = item ? getNextStage(item) : null;
    if (!item || !nextStage) return;
    const nextItem = { ...item, stage: nextStage };
    setItems((current) => current.map((entry) => (entry.id === itemId ? nextItem : entry)));
    syncQueueItem(nextItem, 'PATCH');
  };

  const handleToggleDistribution = (itemId, field, checked) => {
    const item = items.find((entry) => entry.id === itemId);
    if (!item) return;
    const nextItem = {
      ...item,
      distribution: {
        ...DEFAULT_DISTRIBUTION,
        ...(item.distribution || {}),
        [field]: Boolean(checked),
      },
    };
    setItems((current) => current.map((entry) => (entry.id === itemId ? nextItem : entry)));
    syncQueueItem(nextItem, 'PATCH');
  };

  const handleAddResearchToQueue = (result) => {
    openAddDialog({
      title: result.title,
      type: 'youtube',
    });
  };

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setDraft({
      title: item.title || '',
      type: item.type || 'youtube',
      plannedDate: item.plannedDate || '',
    });
    setDialogOpen(true);
  };

  const handleDistribute = (item) => {
    const hasCheckedDistribution = Object.values(item.distribution || {}).some(Boolean);
    if (!hasCheckedDistribution) return;

    const nextItem = { ...item, completedAt: new Date().toISOString() };
    setItems((current) => current.map((entry) => (entry.id === item.id ? nextItem : entry)));
    syncQueueItem(nextItem, 'PATCH');
  };

  const renderActiveColumn = (title, columnItems) => (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/75 p-5 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
          {title}
        </p>
        <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
          {columnItems.length}
        </Badge>
      </div>

      <div className="mt-4 space-y-4">
        {columnItems.length ? columnItems.map((item) => {
        const typeMeta = CONTENT_TYPE_META[item.type] || CONTENT_TYPE_META.youtube;
        const stageMeta = STAGE_META[item.stage] || STAGE_META.idea;
        const nextStage = getNextStage(item);
        const canDistribute = Object.values(item.distribution || {}).some(Boolean);

        return (
          <div
            key={item.id}
            className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 dark:border-white/10 dark:bg-white/[0.04]"
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">{item.title}</p>
                  <Badge className={typeMeta.badgeClass}>{typeMeta.label}</Badge>
                  <Badge className={stageMeta.badgeClass}>{stageMeta.label}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                  <span className="inline-flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatPlannedDate(item.plannedDate)}
                  </span>
                  <span>{`Workflow: ${getStageSequence(item.type).join(' -> ')}`}</span>
                </div>

                {item.stage === 'published' ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                      Distribution
                    </p>
                    <div className="grid gap-3 md:grid-cols-2">
                      {DISTRIBUTION_FIELDS.map((field) => (
                        <label
                          key={`${item.id}-${field.key}`}
                          className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-white/10 dark:bg-[#11161d] dark:text-slate-300"
                        >
                          <Checkbox
                            checked={Boolean(item.distribution?.[field.key])}
                            onCheckedChange={(checked) => handleToggleDistribution(item.id, field.key, checked)}
                          />
                          <span>{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col items-start gap-3 xl:items-end">
                <Button
                  onClick={() => handleEditItem(item)}
                  type="button"
                  variant="outline"
                  className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]"
                >
                  <Edit3 className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                {nextStage ? (
                  <Button
                    onClick={() => handleAdvanceStage(item.id)}
                    type="button"
                    variant="outline"
                    className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 dark:border-fuchsia-400/20 dark:text-fuchsia-200 dark:hover:bg-fuchsia-400/10"
                  >
                    {STAGE_ACTION_LABELS[nextStage] || (STAGE_META[nextStage]?.label || nextStage)}
                  </Button>
                ) : (
                  <div className="space-y-2 xl:text-right">
                    <Button
                      disabled={!canDistribute}
                      onClick={() => handleDistribute(item)}
                      type="button"
                      variant="outline"
                      className={canDistribute
                        ? 'border-emerald-500/20 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/20 dark:text-emerald-300 dark:hover:bg-emerald-500/10'
                        : 'border-slate-200 text-slate-400 opacity-60 hover:bg-transparent hover:text-slate-400 dark:border-white/10 dark:text-slate-500 dark:hover:bg-transparent dark:hover:text-slate-500'}
                    >
                      Distribute
                    </Button>
                    <p className="max-w-xs text-xs text-slate-500 dark:text-slate-400">
                      Check each channel as you publish this piece across platforms.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }) : (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
          {`No active ${title.toLowerCase()} items right now.`}
        </div>
      )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Card className="border border-fuchsia-200 bg-gradient-to-br from-[#fff5ff] via-white to-[#f4f0ff] p-6 dark:border-fuchsia-500/15 dark:bg-gradient-to-br dark:from-[#1d1420] dark:via-[#17121b] dark:to-[#12101a]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-fuchsia-700 dark:text-fuchsia-300">
          Marketing lane
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Run the YouTube-first content waterfall from one operational queue
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-gray-300">
          Track every piece from idea to published, then confirm which downstream distribution moves are complete
          across blog, email, Facebook, and Instagram.
        </p>
        {syncFailed ? (
          <p className="mt-3 text-sm text-amber-700 dark:text-amber-300" role="status">
            Queue changes are preserved in this browser, but server sync needs attention.
          </p>
        ) : null}
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          ['Total pieces', stats.total, Video],
          ['Published', stats.published, CheckCircle2],
          ['YouTube masters', stats.youtube, Video],
          ['Distributed', stats.distributed, Mail],
        ].map(([label, value, Icon]) => (
          <Card key={label} className="border-slate-200 bg-white/90 p-5 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="mb-3 flex items-start justify-between">
              <Icon className="h-6 w-6 text-fuchsia-500 dark:text-fuchsia-300" />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
            <span className="text-2xl font-bold text-slate-900 dark:text-white">{value}</span>
          </Card>
        ))}
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Content queue</h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Move each piece forward, then confirm where it has been distributed once it is live.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setResearchOpen((current) => !current)}
            type="button"
            variant="outline"
            className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]"
          >
            <Search className="mr-2 h-4 w-4" />
            {researchOpen ? 'Hide Content Research' : 'Content Research'}
          </Button>
          <Button
            onClick={() => openAddDialog()}
            type="button"
            className="bg-fuchsia-600 text-white hover:bg-fuchsia-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Content
          </Button>
        </div>
      </div>

      <div className={`grid gap-6 ${researchOpen ? 'xl:grid-cols-[0.95fr_1.25fr]' : ''}`}>
        {researchOpen ? (
          <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                  Content research
                </p>
                <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                  Browse live Reddit conversations for content ideas
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                  Research only. Pull promising topics into the queue with one click, then shape them into a YouTube-first piece.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {RESEARCH_CATEGORIES.map((category) => {
                  const isActive = category.id === activeCategoryId;
                  return (
                    <Button
                      key={category.id}
                      onClick={() => setActiveCategoryId(category.id)}
                      type="button"
                      variant="outline"
                      className={isActive
                        ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-400/40 dark:bg-fuchsia-400/10 dark:text-fuchsia-200'
                        : 'border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]'}
                    >
                      {category.label}
                    </Button>
                  );
                })}
              </div>

              {researchLoading ? (
                <div className="flex items-center gap-2 rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading Reddit research…
                </div>
              ) : null}

              {!researchLoading && researchError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-4 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
                  <span>{researchError} — </span>
                  <button
                    className="font-semibold underline underline-offset-2"
                    onClick={() => loadResearchResults(activeCategory)}
                    type="button"
                  >
                    try again
                  </button>
                </div>
              ) : null}

              {!researchLoading && !researchError ? (
                <div className="space-y-3">
                  {researchResults.length ? researchResults.map((result) => (
                    <div
                      key={result.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.04]"
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
                            {result.subreddit}
                          </Badge>
                          <Badge className="border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                            {result.score} upvotes
                          </Badge>
                        </div>
                        <p className="text-sm font-medium leading-6 text-slate-900 dark:text-white">
                          {truncateTitle(result.title)}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => handleAddResearchToQueue(result)}
                            type="button"
                            size="sm"
                            className="bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                          >
                            Add to Queue
                          </Button>
                          {result.permalink ? (
                            <Button
                              asChild
                              type="button"
                              size="sm"
                              variant="outline"
                              className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]"
                            >
                              <a href={result.permalink} rel="noreferrer" target="_blank">
                                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                                Open
                              </a>
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No Reddit posts matched this category right now.
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Active content pieces</h3>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Earliest planned publish dates stay at the top so the next release is always visible.
              </p>
            </div>
            <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
              {activeItems.length} active
            </Badge>
          </div>

          {!activeItems.length ? (
            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
              Add your first content piece to start tracking the waterfall.
            </div>
          ) : (
            <div className={researchOpen ? 'grid grid-cols-1 gap-6' : 'grid grid-cols-2 gap-6'}>
              {renderActiveColumn('YouTube', youtubeItems)}
              {renderActiveColumn('Blog', blogItems)}
            </div>
          )}
        </Card>
      </div>

      <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
        <button
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={() => setCompletedOpen((current) => !current)}
          type="button"
        >
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Completed</h3>
            <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
              {completedItems.length}
            </Badge>
          </div>
          {completedOpen ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
        </button>

        {completedOpen ? (
          <div className="mt-6 space-y-4">
            {completedItems.length ? completedItems.map((item) => {
              const typeMeta = CONTENT_TYPE_META[item.type] || CONTENT_TYPE_META.youtube;
              const checkedLabels = getCheckedDistributionLabels(item.distribution);

              return (
                <div
                  key={`${item.id}-completed`}
                  className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 dark:border-white/10 dark:bg-white/[0.04]"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{item.title}</p>
                    <Badge className={typeMeta.badgeClass}>{typeMeta.label}</Badge>
                  </div>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                    Completed {formatCompletedDate(item.completedAt)}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {checkedLabels.length ? checkedLabels.map((label) => (
                      <Badge
                        key={`${item.id}-${label}`}
                        className="border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      >
                        {label}
                      </Badge>
                    )) : (
                      <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
                        No channels marked
                      </Badge>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                No completed pieces yet.
              </div>
            )}
          </div>
        ) : null}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editingItemId ? 'Edit content piece' : 'Add content piece'}</DialogTitle>
            <DialogDescription>
              {editingItemId
                ? 'Update the title or planned date without changing the piece type or stage.'
                : 'Add the next YouTube master or standalone blog piece to the weekly content waterfall.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="marketing-content-title">Title</Label>
              <Input
                id="marketing-content-title"
                value={draft.title}
                onChange={(event) => updateDraft('title', event.target.value)}
                placeholder="Weekly YouTube title or blog headline"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="marketing-content-type">Type</Label>
                <select
                  id="marketing-content-type"
                  value={draft.type}
                  disabled={Boolean(editingItemId)}
                  onChange={(event) => updateDraft('type', event.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="youtube">YouTube</option>
                  <option value="blog">Blog</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="marketing-content-date">Planned publish date</Label>
                <Input
                  id="marketing-content-date"
                  type="date"
                  value={draft.plannedDate}
                  onChange={(event) => updateDraft('plannedDate', event.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => {
                setDialogOpen(false);
                resetDraft();
              }}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button onClick={handleAddItem} type="button">
              {editingItemId ? 'Save Changes' : 'Add to Queue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
